import { useEffect, useState } from "react";
import { useDataProvider, useListContext } from "ra-core";
import { useNavigate } from "react-router";
import { History, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DEFAULT_ROUND_SIZE, MAX_ROUND_SIZE } from "./nearbyRound";
import { fetchNearbyStops } from "./nearbyRoundQuery";
import { hasOpenRound } from "./roundPersistence";
import type { RoutePoint } from "./nearestNeighborRoute";

const clampSize = (v: number) =>
  Number.isFinite(v)
    ? Math.min(MAX_ROUND_SIZE, Math.max(1, Math.round(v)))
    : DEFAULT_ROUND_SIZE;

const getPosition = (): Promise<RoutePoint> =>
  new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(
        new Error(
          "Tu navegador no soporta ubicación. Selecciona empresas a mano y usa «Armar ruta».",
        ),
      );
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? "No diste permiso de ubicación. Puedes seleccionar empresas a mano y usar «Armar ruta»."
              : "No se pudo obtener tu ubicación. Puedes seleccionar empresas a mano y usar «Armar ruta».",
          ),
        ),
      { timeout: 15000, maximumAge: 60000 },
    );
  });

/**
 * Modo A, "Ronda cerca de mí" (PLAN_RUTAS.md §A): pide la ubicación SOLO al tocar el
 * botón (permiso explícito del navegador), toma el filtro activo del Directorio, busca
 * las N empresas más cercanas y abre /directory/ruta con ellas. Las coordenadas viajan
 * solo en el router state, nunca se guardan.
 */
export const RoundStart = () => {
  const { filterValues } = useListContext();
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(String(DEFAULT_ROUND_SIZE));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canResume, setCanResume] = useState(false);

  useEffect(() => {
    let alive = true;
    hasOpenRound().then(
      (yes) => alive && setCanResume(yes),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, []);

  const start = async () => {
    setError(null);
    setBusy(true);
    try {
      const center = await getPosition();
      const n = clampSize(Number(size));
      const stops = await fetchNearbyStops({
        dataProvider,
        filter: filterValues,
        center,
        n,
      });
      if (stops.length === 0) {
        setError(
          "No encontré empresas cerca de ti con estos filtros. Prueba quitar algún filtro.",
        );
        return;
      }
      navigate("/directory/ruta", {
        state: { stops, start: center, filter: filterValues, size: n },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {canResume && (
        <Button variant="outline" onClick={() => navigate("/directory/ruta")}>
          <History /> Retomar ronda abierta
        </Button>
      )}
      <Button variant="outline" onClick={() => setOpen(true)}>
        <LocateFixed /> Ronda cerca de mí
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ronda cerca de mí</DialogTitle>
            <DialogDescription>
              Arma una ruta con las empresas más cercanas a tu ubicación que
              cumplan los filtros de arriba. Sin visitas previas ni descartadas.
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1 text-sm font-medium">
            ¿Cuántas paradas?
            <input
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_ROUND_SIZE}
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="h-10 rounded-lg border bg-card px-3 text-base"
            />
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <Button className="h-12" disabled={busy} onClick={start}>
            {busy ? "Buscando…" : "Usar mi ubicación y armar la ronda"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
